import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container,
    Box,
    Alert,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import { getMonthlyAverages, getAvailableZones } from '../api/monthlyAverages';
import MonthlyControlBar from './monthly/MonthlyControlBar';
import MonthlyKpiCards from './monthly/MonthlyKpiCards';
import MonthlyTrendChart from './monthly/MonthlyTrendChart';
import MonthlyZoneComparison from './monthly/MonthlyZoneComparison';
import MonthlyBreakdownTable from './monthly/MonthlyBreakdownTable';
import MonthlyLoadingSkeleton from './monthly/MonthlyLoadingSkeleton';
import MonthlyEmptyState from './monthly/MonthlyEmptyState';
import {
    normalizeMonthlyData,
    calculateKpiStats,
    build12MonthTrendData,
    buildMonthZoneComparisonData,
} from '../utils/monthlyDataUtils';

function MonthlyAveragesPage() {
    const { t } = useTranslation();

    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState('all'); // 'all' or 1..12
    const [selectedZone, setSelectedZone] = useState('all'); // 'all' or zone name
    const [activeMeasure, setActiveMeasure] = useState('ALL'); // 'ALL' or 'TEMP' | 'HUMI' | 'POWE' | 'PRES'

    // Chart & view preferences
    const [chartType, setChartType] = useState('line'); // 'line' | 'area' (when all) or 'bar' | 'cards' (when month)
    const [trendMode, setTrendMode] = useState('byZone'); // 'byZone' | 'byMetric'

    const [availableZones, setAvailableZones] = useState([]);
    const [rawAverages, setRawAverages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Fetch available zones on mount
    useEffect(() => {
        let isMounted = true;
        const fetchZones = async () => {
            const zones = await getAvailableZones();
            if (isMounted && zones.length > 0) {
                setAvailableZones(zones);
            }
        };
        fetchZones();
        return () => {
            isMounted = false;
        };
    }, []);

    // Fetch monthly averages from API
    const fetchMonthlyData = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const data = await getMonthlyAverages({
                year: selectedYear,
                month: selectedMonth,
                zone: selectedZone,
            });

            setRawAverages(data || []);

            // Also update available zones if new ones appear in data
            if (Array.isArray(data) && data.length > 0) {
                const zonesFromData = [...new Set(data.map((d) => d.zone))].filter(Boolean);
                setAvailableZones((prev) => [...new Set([...prev, ...zonesFromData])].sort());
            }
        } catch (err) {
            console.error('Failed to fetch monthly averages:', err);
            setError(err.response?.data?.message || err.message || 'Error fetching monthly data');
            setRawAverages([]);
        } finally {
            setLoading(false);
        }
    }, [selectedYear, selectedMonth, selectedZone]);

    useEffect(() => {
        fetchMonthlyData();
    }, [fetchMonthlyData]);

    // Handle switching between 'all' months and a specific month to update appropriate default chartType
    const handleMonthChange = (newMonth) => {
        setSelectedMonth(newMonth);
        if (newMonth === 'all') {
            if (chartType === 'bar' || chartType === 'cards') {
                setChartType('line');
            }
        } else {
            if (chartType === 'line' || chartType === 'area') {
                setChartType('bar');
            }
        }
    };

    const handleResetFilters = () => {
        setSelectedYear(currentYear);
        setSelectedMonth('all');
        setSelectedZone('all');
        setActiveMeasure('ALL');
        setChartType('line');
    };

    // Normalized data
    const normalizedData = useMemo(() => {
        return normalizeMonthlyData(rawAverages);
    }, [rawAverages]);

    // KPI stats
    const kpiStats = useMemo(() => {
        return calculateKpiStats(normalizedData, activeMeasure, t);
    }, [normalizedData, activeMeasure, t]);

    // 12-Month Trend Data
    const trendData = useMemo(() => {
        return build12MonthTrendData(normalizedData, {
            activeMeasure,
            selectedZone,
            t,
        });
    }, [normalizedData, activeMeasure, selectedZone, t]);

    // Month Zone Comparison Data
    const comparisonData = useMemo(() => {
        return buildMonthZoneComparisonData(normalizedData, {
            activeMeasure,
            t,
        });
    }, [normalizedData, activeMeasure, t]);

    const hasData = normalizedData.length > 0;

    return (
        <Box sx={{ flexGrow: 1, minHeight: '100vh', pb: 6 }}>
            <Header title={t('common.monthlyAveragesTitle', 'Monthly Sensor Averages')} />

            <Container maxWidth="xl" sx={{ mt: 3, px: { xs: 2, sm: 3 } }}>
                {/* Page Title & Subtitle */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: -0.5, mb: 0.5 }}>
                        {t('common.monthlyAveragesTitle', 'Monthly Sensor Averages')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t(
                            'common.monthlyAveragesSubtitle',
                            'Track and analyze long-term climate, power, and atmospheric patterns across your home zones'
                        )}
                    </Typography>
                </Box>

                {/* Filter & Control Bar */}
                <MonthlyControlBar
                    selectedYear={selectedYear}
                    onYearChange={setSelectedYear}
                    selectedMonth={selectedMonth}
                    onMonthChange={handleMonthChange}
                    selectedZone={selectedZone}
                    onZoneChange={setSelectedZone}
                    availableZones={availableZones}
                    activeMeasure={activeMeasure}
                    onMeasureChange={setActiveMeasure}
                    chartType={chartType}
                    onChartTypeChange={setChartType}
                    trendMode={trendMode}
                    onTrendModeChange={setTrendMode}
                    onRefresh={fetchMonthlyData}
                    loading={loading}
                />

                {/* Error Banner */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Content Section */}
                {loading ? (
                    <MonthlyLoadingSkeleton />
                ) : !hasData ? (
                    <MonthlyEmptyState
                        onResetFilters={handleResetFilters}
                        selectedYear={selectedYear}
                        selectedMonth={selectedMonth}
                        selectedZone={selectedZone}
                    />
                ) : (
                    <>
                        {/* KPI Cards */}
                        <MonthlyKpiCards
                            stats={kpiStats}
                            activeMeasure={activeMeasure}
                            onSelectMeasure={setActiveMeasure}
                        />

                        {/* Visualizations */}
                        {selectedMonth === 'all' ? (
                            <MonthlyTrendChart
                                trendData={trendData}
                                chartType={chartType}
                                activeMeasure={activeMeasure}
                                selectedYear={selectedYear}
                                selectedZone={selectedZone}
                            />
                        ) : (
                            <MonthlyZoneComparison
                                comparisonData={comparisonData}
                                normalizedData={normalizedData}
                                selectedMonth={selectedMonth}
                                selectedYear={selectedYear}
                                activeMeasure={activeMeasure}
                                viewMode={chartType}
                            />
                        )}

                        {/* Breakdown Data Table */}
                        <MonthlyBreakdownTable
                            normalizedData={normalizedData}
                            selectedYear={selectedYear}
                        />
                    </>
                )}
            </Container>
        </Box>
    );
}

export default MonthlyAveragesPage;
